"use client";

import { motion, useReducedMotion } from "@/lib/motion";
import { CheckCircle2 } from "@/components/icons";
import { STATS } from "@/lib/constants";
import HeroVisual from "../hero/hero-visual";

type ProxyType = "mobile" | "residential";

interface ProxyTesterSectionProps {
  defaultProxyType?: ProxyType;
}

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

const COPY = {
  mobile: {
    label: "Live Proxy Demo",
    heading: "See Mobile Proxies in Action",
    sub: "Enter any domain below and watch our live 4G/5G mobile proxy network route your request in milliseconds. No account, no credit card — just real results from premium pooled mobile IPs.",
    bullets: [
      "Premium pooled mobile IPs",
      "99.5%+ success rate across all targets",
      "Ethically sourced & fully compliant",
      "Target by city, ISP and country",
      "On-demand IP rotation",
    ],
    stats: [
      { value: "Millions of", label: "Mobile IPs" },
      { value: "99.5%", label: "Success rate" },
      { value: "5 min", label: "Setup time" },
    ],
  },
  residential: {
    label: "Live Proxy Demo",
    heading: "See Residential Proxies in Action",
    sub: `Enter any domain below and watch premium, ethically sourced residential IPs route your request from ${STATS.countries} countries. No account, no credit card — just real results.`,
    bullets: [
      `${STATS.ips} ethically sourced residential IPs`,
      `${STATS.countries} countries, state & city targeting`,
      "99.9%+ success rate on all targets",
      "Rotating & sticky sessions available",
      "Pay only for the bandwidth you use",
      "No contracts — KYC required",
    ],
    stats: [
      { value: STATS.ips, label: "Residential IPs" },
      { value: STATS.countries, label: "Countries" },
      { value: STATS.uptime, label: "Success rate" },
    ],
  },
};

export default function ProxyTesterSection({
  defaultProxyType = "mobile",
}: ProxyTesterSectionProps) {
  const prefersReducedMotion = useReducedMotion();
  const copy = COPY[defaultProxyType];

  return (
    <section
      className="py-section-sm lg:py-section relative overflow-hidden sm:py-20"
      aria-label="Proxy tester"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <motion.div
            variants={containerVariants}
            initial={prefersReducedMotion ? "visible" : "hidden"}
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="flex flex-col items-start text-left"
          >
            <motion.span
              variants={itemVariants}
              className="text-muted-foreground mb-3 text-xs font-semibold"
            >
              {copy.label}
            </motion.span>

            <motion.h2
              variants={itemVariants}
              className="text-foreground mb-4 text-2xl leading-tight font-bold tracking-tight sm:text-3xl md:text-4xl"
            >
              {copy.heading}
            </motion.h2>

            <motion.p
              variants={itemVariants}
              className="text-muted-foreground mb-7 max-w-md text-base leading-relaxed"
            >
              {copy.sub}
            </motion.p>

            <motion.ul
              variants={itemVariants}
              className="mb-8 flex w-full flex-col gap-2.5"
              aria-label="Key features"
            >
              {copy.bullets.map((item) => (
                <li key={item} className="text-muted-foreground flex items-center gap-3 text-sm">
                  <CheckCircle2
                    size={14}
                    className="shrink-0"
                    style={{ color: "var(--brand-green)" }}
                    aria-hidden="true"
                  />
                  {item}
                </li>
              ))}
            </motion.ul>

            <motion.div
              variants={itemVariants}
              className="border-border/50 flex w-full items-center justify-start gap-6 border-t pt-6"
            >
              {copy.stats.map(({ value, label }) => (
                <div key={label} className="flex flex-col gap-0.5">
                  <span className="text-foreground text-xl font-bold tabular-nums">{value}</span>
                  <span className="text-muted-foreground text-xs">{label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <HeroVisual defaultNetwork={defaultProxyType} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
